import { usePortalMessage, type PortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Sign up / Log in"` — `/log-in` and `/sign-up`, drawn in the PUBLIC design since the owner's
 * decision of 2026-10-11 (ADR 0056's portal amendment, 2026-10-12): the owner's export `wetalk-login.html` and the
 * prototype's restyled screen — one card, "Sign in or create your account", email first.
 *
 * ── EMAIL FIRST, ON THE EXISTING API ─────────────────────────────────────────────────────────────────────────
 *
 * Both routes open on the same card — Google, Microsoft, or a work email. "Continue with email" asks
 * `POST /v1/auth/discover` about the address, exactly as the identifier-first step always did (P5-05, amendment A4),
 * and only then does the card open anything under the address: an SSO-governed address is never shown a password
 * field (design §8.3). A changed address closes what was opened and asks again.
 *
 * WHY WHAT OPENS IS NOT CHOSEN BY WHETHER THE ADDRESS HAS AN ACCOUNT. Discovery is enumeration-safe by construction —
 * an unknown domain, a known domain without SSO and a malformed address get the same answer, padded to the same time
 * — so the screen cannot know, and must not learn, whether a person exists. Branching "existing account → password,
 * new address → sign-up" would need exactly the oracle discovery refuses to be. So the fields are the ones the person
 * came for: "Log in" opens the password (with "Forgot your password?", which opens `/forgot-password` since
 * 2026-10-12), "Start free" opens the business and a new password, and each offers the other in one press under the form (the prototype's "New here? Create an account
 * instead." / "Already have an account? Log in.") with the address kept. What the screen reveals is unchanged: a wrong
 * password or an unknown address get the API's one answer; a sign-up for an address that already signs in is refused
 * `email_taken` after the whole form, rate-limited, as before (SECURITY-REVIEW.md records that residual).
 *
 * "Continue with Google" and "Continue with Microsoft" — AMENDED 2026-10-25 (plan-028, design §39.11, ADR 0080): each
 * button is a plain link to its provider's start route (`/v1/auth/{google,microsoft}/start`, a top-level navigation)
 * when `GET /v1/auth/providers` says that provider's switch is on; otherwise it stays as drawn, DISABLED with "coming
 * soon" (a button never leads to a route that would refuse it). The provider brings the browser back here:
 *   `?provider=signed_in`   signed in — the context is read and the same steps as after a password follow (the second
 *                           step, a chooser, "Finish setting up your business");
 *   `?confirm=provider`     the provider is not authoritative for the address: a six-digit code was mailed
 *                           (`confirmProviderSignIn`, `resendProviderSignInCode`);
 *   `?problem=<code>`       one message per refusal, never naming a person; an SSO-governed address also gets its
 *                           organization's start (`sso_start`, accepted only as that route).
 * The query is dropped from the address bar once read. An SSO-governed address still gets its "Continue with
 * Microsoft" link from discovery, as before.
 *
 * ── AFTER THE PASSWORD (unchanged) ───────────────────────────────────────────────────────────────────────────
 *
 *   · a sign-up (business name, country chosen — never proposed — and time zone proposed from the browser, C20)
 *     creates the person and sends the verification email; AMENDED 2026-10-17 (ADR 0071, verification-first) it signs
 *     nobody in and founds nothing, so the card says "Check your email": open the link, then log in, and "Finish
 *     setting up your business" — pre-filled with what was typed here — creates the business. (A sign-up that
 *     answers signed in — the sandbox, an older API — opens first run as before);
 *   · a sign-in that lands UNSCOPED (`AuthSessionUnscopedError`): no account → "Finish setting up your business"
 *     (`createAccount`); one → switched to it at once; several → the console's own chooser decides (plan-014 A4);
 *   · THE SECOND STEP (plan-014 C2): after the password or an emailed code, while the person's authenticator is
 *     enforced and unproven, its code or a recovery code (`answerMfaChallenge`) before the console opens — the API
 *     refuses every organization page `401 assurance_required` until then;
 *   · EMAIL ME A SIGN-IN CODE (plan-014 C1): beside the password, only when the API says a code can be sent; same
 *     answer for a known and an unknown address; never a second step.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Field, Icon, Input, Mono } from "@wetalk/ui";

import "./auth.css";
import type { DiscoveryOutcome } from "@wetalk/domain";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { AuthSessionUnscopedError, type AuthSessionView } from "../../api/gateway.js";
import type { SecondStepFactor } from "../../api/slice/personal-security.js";
import { referenceOf, signInProblemOf } from "../../api/server-fault.js";
import { ApiError } from "@wetalk/api-client";
import { useGateway } from "../../api/provider.js";

import { useAdoptAuthSession } from "./adopt-auth-session.js";
import { plansSearchOf } from "../finance/plans/plans-screen.js";
import {
  BusinessFields,
  businessDraftProblem,
  emptyBusinessDraft,
  type BusinessDraft,
} from "./business-fields.js";
import { FinishSetup } from "./finish-setup.js";
import { entryGuardOf } from "./identity-challenge.js";
import { appSignInReturnOf, tabStorage } from "../app-sign-in/app-sign-in-request.js";
import { ArrowMark, EnvelopeMark, GoogleMark, MicrosoftMark, PublicFrame, PublicKicker, usePublicSiteLink } from "./public-frame.js";

export type EntryIntent = "sign-up" | "log-in";

export interface AuthScreenProps {
  readonly intent: EntryIntent;
  /**
   * plan-020 WAPP-BILL (ED-22, OD-14): the plan the person chose on the website (`/sign-up?plan=<offer code>`), carried
   * through the entry steps; once the console opens it lands on that plan's page (`/finance/plans?offer=<code>`, which
   * shows the chooser for a code that is not sellable) instead of the first-run page or the dashboard.
   */
  readonly offerCode?: string;
}

/** The entry routes' search: `?plan=` when it has an offer code's shape (anything else is dropped). */
export interface EntrySearch {
  readonly plan?: string;
}

/** The entry routes' `validateSearch`, total over the untrusted address bar. */
export function entrySearchOf(raw: Readonly<Record<string, unknown>>): EntrySearch {
  const offer = plansSearchOf({ offer: raw["plan"] }).offer;
  return offer === undefined ? {} : { plan: offer };
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MINIMUM_PASSWORD = 10;

/** The steps drawn in the one entry card: the address, then what discovery opened under it. */
const ENTRY_STEP: ReadonlySet<string> = new Set(["start", "governed", "log-in", "sign-up"]);

/**
 * Moves focus when the step changes (never on the first paint):
 *   · inside the entry card, to the first field discovery opened under the address (the business name or the
 *     password), or to the SSO notice — the card itself stays, so its heading is not read again;
 *   · back to the bare address from another card (Change on the emailed-code step), to the address field — but not
 *     while the person is typing a new address into it;
 *   · onto any other card (the second step, the emailed code, finishing the business), to its heading, so a
 *     screen-reader user hears where they are and a keyboard user continues from the top.
 */
function useStepFocus(step: string): {
  readonly card: RefObject<HTMLDivElement | null>;
  readonly more: RefObject<HTMLDivElement | null>;
} {
  const card = useRef<HTMLDivElement | null>(null);
  const more = useRef<HTMLDivElement | null>(null);
  const previous = useRef(step);
  useEffect(() => {
    const from = previous.current;
    if (from === step) return;
    previous.current = step;
    if (step === "start") {
      if (!ENTRY_STEP.has(from)) card.current?.querySelector<HTMLElement>("input")?.focus();
      return;
    }
    if (ENTRY_STEP.has(step)) {
      more.current?.querySelector<HTMLElement>("input, select, [tabindex='-1']")?.focus();
      return;
    }
    card.current?.querySelector<HTMLElement>("h1")?.focus();
  }, [step]);
  return { card, more };
}

export function AuthScreen({ intent, offerCode }: AuthScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const navigate = useNavigate();
  const adoptAuthSession = useAdoptAuthSession();
  const providerNoteId = useId();
  const site = usePublicSiteLink();
  /** plan-028: which provider buttons work. A failed read keeps them disabled, as when both switches are off. */
  const providers = useQuery({
    queryKey: ["auth", "sign-in-providers"],
    queryFn: () => gateway.readSignInProviders(),
    staleTime: 60_000,
    retry: false,
  });
  /** plan-028: what a provider's return asked for, read once from the address bar. */
  const [providerReturn] = useState<ProviderReturn>(() => providerReturnOf(window.location.search));
  const [providerConfirming, setProviderConfirming] = useState(providerReturn.confirm);
  /** plan-028: the refusal a provider's return named, shown on the card until the person acts. */
  const [providerProblem, setProviderProblem] = useState(providerReturn.problem);

  /** The path after the address: the route's own, until the person switches with the link under the form. */
  const [mode, setMode] = useState<EntryIntent>(intent);
  const [accountDraft, setAccountDraft] = useState<BusinessDraft>(emptyBusinessDraft);
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  /**
   * The sign-up trap (ADR 0071): the value of the one field nobody sees. A person never types into it; a script that
   * fills every field does, and the API then answers as usual and creates nothing. Sent with every step of this card,
   * beside the step's own solved identity challenge (`./identity-challenge.ts`).
   */
  const [entryMarker, setEntryMarker] = useState("");
  const [discovery, setDiscovery] = useState<DiscoveryOutcome | null>(null);
  const [problem, setProblemText] = usePortalMessage();
  /** 2026-10-08: the request id of a failure on WeTalk's side, quoted small for support. */
  const [problemReference, setProblemReference] = useState<string | null>(null);
  const setProblem = (text: PortalMessage | null): void => {
    setProblemText(text);
    setProblemReference(null);
  };
  /**
   * A step that failed: a server fault or an unreachable network in sign-in's own plain words, never
   * the server's sentence (the half-open incident showed "The shared cache did not answer…" here);
   * anything written for the person — a wrong password, an invalid code — as it was written.
   */
  const reportFailure = (raised: Error): void => {
    setProblemText(() => (signInProblemOf(raised)));
    setProblemReference(referenceOf(raised));
  };
  /** The emailed-code path (plan-014 C1): null until "Email me a sign-in code" is chosen. */
  const [signInCode, setSignInCode] = useState<{ readonly expiresAt: string } | null>(null);
  const [code, setCode] = useState("");
  /** ADR 0071: set when a sign-up answered `verification_sent` — the address the link went to. */
  const [verificationSentTo, setVerificationSentTo] = useState<string | null>(null);
  /** Set when the sign-in landed unscoped: the accounts the person may open (maybe none). */
  const [unscoped, setUnscoped] = useState<AuthSessionUnscopedError["accountChoice"] | null>(
    null,
  );

  const signingUp = mode === "sign-up";
  const landing = signingUp ? "/first-run" : "/dashboard";
  /** Where the console opens: the chosen plan's page when the person came from a plan on the website. */
  const land = async (): Promise<void> => {
    if (offerCode === undefined) await navigate({ to: landing });
    else await navigate({ to: "/finance/plans", search: { offer: offerCode } });
  };

  /** plan-014 C2: a sign-in that still owes its second step, held here until the code is given. */
  const [secondStep, setSecondStep] = useState<AuthSessionView | null>(null);
  const [secondStepFactor, setSecondStepFactor] = useState<SecondStepFactor>("totp");

  /** Opens the console with `opened` — after its second step, when the person's authenticator asks for one. */
  const proceed = async (opened: AuthSessionView): Promise<void> => {
    const methods = await gateway.readMySignInMethods();
    if (methods.secondStepEnforced && !methods.secondStepVerified) {
      setCode("");
      setProblem(null);
      setSecondStep(opened);
      return;
    }
    adoptAuthSession(opened);
    await land();
  };

  const answerSecondStep = useMutation({
    mutationFn: () => gateway.answerMfaChallenge(secondStepFactor, code.replace(/\s/g, "")),
    onSuccess: async () => {
      const opened = secondStep;
      if (opened === null) return;
      adoptAuthSession(opened);
      await land();
    },
    onError: (raised: Error) => {
      if (raised instanceof ApiError && raised.code === "mfa_factor_invalid") {
        setProblem(
          () => (secondStepFactor === "totp"
            ? portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e")
            : portalText("core", "auth.that_recovery_code_is_not_right_or_it_was_used_already_916ef9")),
        );
        return;
      }
      reportFailure(raised);
    },
  });

  // Whether an emailed code can be sent at all (plan-014 C1). A failed read offers nothing rather than a control
  // that cannot work.
  const codeAvailability = useQuery({
    queryKey: ["personal-security", "sign-in-code-availability"],
    queryFn: () => gateway.readSignInCodeAvailability(),
    enabled: mode === "log-in",
    staleTime: 300_000,
  });
  const codeAvailable = mode === "log-in" && codeAvailability.data === true;

  const askForCode = useMutation({
    mutationFn: () => gateway.requestSignInCode(emailAddress),
    onSuccess: (asked) => {
      setProblem(null);
      setCode("");
      setSignInCode(asked);
    },
    onError: (raised: Error) => {
      reportFailure(raised);
    },
  });

  const signInWithCode = useMutation({
    mutationFn: () => gateway.verifySignInCode(code.replace(/\s/g, "")),
    onSuccess: async (opened) => {
      // An emailed code is never the second step (plan-014 C2): a person with an authenticator is asked for it next.
      await proceed(opened);
    },
    onError: (raised: Error) => {
      if (raised instanceof AuthSessionUnscopedError) {
        onUnscoped(raised.accountChoice);
        return;
      }
      reportFailure(raised);
    },
  });

  // Several organizations (plan-014 A4): the shell's chooser decides, so the signed-in context is read as it
  // stands (`selection_required`), written to the cache before navigating (the guard reads it), and the console opens.
  const openChooser = useMutation({
    mutationFn: () => gateway.currentAuthSession(),
    onSuccess: async (current) => {
      if (current === null) {
        setProblem(() => (portalText("core", "auth.you_are_signed_out_log_in_again_c4d978")));
        return;
      }
      adoptAuthSession(current);
      await land();
    },
    onError: (raised: Error) => {
      reportFailure(raised);
    },
  });

  /**
   * An unscoped sign-in: open the one organization at once, let the console choose between several, or finish setup.
   * plan-034 WAPP-APPSIGNIN: a person with no organization who signs in for the wetalk app is taken back to the app's
   * consent page instead (through the shell's guard), and consents without one — the app shows its "nothing to show".
   */
  const onUnscoped = (accountChoice: AuthSessionUnscopedError["accountChoice"]): void => {
    const [only, ...rest] = accountChoice;
    if ((only !== undefined && rest.length > 0) || (only === undefined && signingInForApp())) {
      openChooser.mutate();
      return;
    }
    setUnscoped(accountChoice);
    if (only !== undefined) choose.mutate(only.accountId);
  };

  const choose = useMutation({
    mutationFn: (accountId: string) => gateway.switchAccount(accountId, null),
    onSuccess: async (opened) => {
      adoptAuthSession(opened);
      await land();
    },
    onError: (raised: Error) => {
      reportFailure(raised);
    },
  });

  // plan-028: after a provider signed the person in (or a code finished it), the same steps as after a password.
  const continueAfterProvider = useMutation({
    mutationFn: () => gateway.currentAuthSession(),
    onSuccess: async (current) => {
      if (current === null) {
        setProblem(() => (portalText("core", "auth.you_are_signed_out_log_in_again_c4d978")));
        return;
      }
      // No organization yet (a provider sign-up): "Finish setting up your business", as after a password sign-up.
      if (current.kind === "selection_required" && current.accountChoice.length === 0 && !signingInForApp()) {
        setUnscoped([]);
        return;
      }
      await proceed(current);
    },
    onError: (raised: Error) => {
      if (raised instanceof AuthSessionUnscopedError) {
        onUnscoped(raised.accountChoice);
        return;
      }
      reportFailure(raised);
    },
  });

  const confirmProvider = useMutation({
    mutationFn: () => gateway.confirmProviderSignIn(code.replace(/\s/g, "")),
    onSuccess: () => {
      setProviderConfirming(false);
      setCode("");
      continueAfterProvider.mutate();
    },
    onError: (raised: Error) => {
      if (raised instanceof ApiError && raised.code === "provider_code_invalid") {
        setProblem(() => (portalText("core", "auth.provider.code_wrong")));
        return;
      }
      if (raised instanceof ApiError && (raised.code === "provider_sign_in_expired" || raised.code === "provider_code_limit" || raised.code === "provider_sign_in_refused")) {
        setProviderConfirming(false);
        setProblem(() => (providerProblemText(raised.code === "provider_code_limit" ? "too_many_codes" : "expired")));
        return;
      }
      reportFailure(raised);
    },
  });

  const resendProviderCode = useMutation({
    mutationFn: () => gateway.resendProviderSignInCode(),
    onSuccess: () => {
      setCode("");
      setProblem(() => (portalText("core", "auth.provider.resent")));
    },
    onError: (raised: Error) => {
      if (raised instanceof ApiError && (raised.code === "provider_code_limit" || raised.code === "provider_sign_in_expired")) {
        setProviderConfirming(false);
        setProblem(() => (providerProblemText(raised.code === "provider_code_limit" ? "too_many_codes" : "expired")));
        return;
      }
      reportFailure(raised);
    },
  });

  // plan-028: act on the provider's return once, then drop it from the address bar (a reload must not repeat it).
  const handledProviderReturn = useRef(false);
  useEffect(() => {
    if (handledProviderReturn.current) return;
    handledProviderReturn.current = true;
    if (providerReturn.signedIn) continueAfterProvider.mutate();
    if (providerReturn.signedIn || providerReturn.problem !== null || providerReturn.confirm) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const discover = useMutation({
    mutationFn: async (address: string) => gateway.discoverIdentifier(address, await entryGuardOf(gateway, entryMarker)),
    onSuccess: (outcome) => {
      setDiscovery(outcome);
    },
    onError: (raised: Error) => {
      reportFailure(raised);
    },
  });

  const authenticate = useMutation({
    /** The opened auth_session, or `null` for a sign-up whose verification mail is on its way (ADR 0071). */
    mutationFn: async (): Promise<AuthSessionView | null> => {
      // ADR 0071: each step carries the trap's value and its own solved identity challenge.
      const guard = await entryGuardOf(gateway, entryMarker);
      if (!signingUp) return gateway.logIn({ emailAddress, password, guard });
      // `onPassword` refuses a draft without a country before this can run.
      if (accountDraft.country === null) throw new Error(portalText("core", "auth.choose_the_country_the_business_is_in_aa9bca"));
      const outcome = await gateway.signUp({
        accountName: accountDraft.accountName.trim(),
        emailAddress,
        password,
        country: accountDraft.country,
        timeZone: accountDraft.timeZone,
        guard,
      });
      return outcome.kind === "signed_in" ? outcome.authSession : null;
    },
    onSuccess: async (opened) => {
      if (opened === null) {
        setPassword("");
        setVerificationSentTo(emailAddress);
        return;
      }
      // Before navigating: the guard must not read a visit's cached "nobody" (review row #104). A log-in owes its
      // second step first when the person's authenticator asks for one (plan-014 C2).
      if (!signingUp) {
        await proceed(opened);
        return;
      }
      adoptAuthSession(opened);
      await land();
    },
    onError: (raised: Error) => {
      if (raised instanceof AuthSessionUnscopedError) {
        onUnscoped(raised.accountChoice);
        return;
      }
      reportFailure(raised);
    },
  });

  const onIdentifier = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setProblem(null);
    setProviderProblem(null);
    if (!EMAIL_SHAPE.test(emailAddress)) {
      setProblem(() => (portalText("core", "auth.that_does_not_look_like_an_email_address_8b7893")));
      return;
    }
    discover.mutate(emailAddress);
  };

  const onPassword = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setProblem(null);
    if (signingUp) {
      const businessProblem = businessDraftProblem(accountDraft);
      if (businessProblem !== null) {
        setProblem(() => (businessDraftProblem(accountDraft) ?? businessProblem));
        return;
      }
      if (password.length < MINIMUM_PASSWORD) {
        setProblem(() => (portalText("core", "auth.passwords_are_at_least_10_characters_ce1196")));
        return;
      }
    }
    authenticate.mutate();
  };

  /** Back to the first step, the address kept in its field. */
  const changeAddress = (): void => {
    setDiscovery(null);
    setSignInCode(null);
    setPassword("");
    setCode("");
    setProblem(null);
  };

  /** The other path after the address — the address and the discovery answer kept. */
  const switchMode = (next: EntryIntent): void => {
    setMode(next);
    setPassword("");
    setProblem(null);
  };

  const step =
    secondStep !== null ? "second-step"
      : providerConfirming ? "provider-confirm"
      : verificationSentTo !== null ? "verification-sent"
      : unscoped !== null ? (unscoped.length === 0 ? "finish-setup" : "choose")
        : signInCode !== null ? "email-code"
          : discovery === null ? "start"
            : !discovery.showPassword ? "governed"
              : mode;
  const { card, more } = useStepFocus(step);

  const identifier = (
    <div className="c-entry-identifier">
      <span className="c-entry-identifier-address">{emailAddress}</span>
      <Button variant="ghost" onClick={changeAddress}>{portalText("core", "auth.change_64fbd9")}</Button>
    </div>
  );

  return (
    <PublicFrame>
      <div className="pd-form" ref={card}>
        {secondStep !== null ? (
          <form
            aria-labelledby="pd-second-step-title"
            onSubmit={(event) => {
              event.preventDefault();
              setProblem(null);
              if (code.trim() === "") {
                setProblem(() => (secondStepFactor === "totp" ? portalText("core", "auth.enter_the_6_digit_code_from_your_authenticator_app_ee11fc") : portalText("core", "auth.enter_one_of_your_recovery_codes_44618f")));
                return;
              }
              answerSecondStep.mutate();
            }}
          >
            <h1 id="pd-second-step-title" tabIndex={-1}>{portalText("core", "auth.your_second_step_32986d")}</h1>
            <p className="c-entry-code-note">
              {secondStepFactor === "totp"
                ? portalText("core", "auth.enter_the_code_your_authenticator_app_shows_for_wetalk_a69c9b")
                : portalText("core", "auth.enter_one_of_the_recovery_codes_you_saved_each_works_once_4d821d")}
            </p>
            <Field label={secondStepFactor === "totp" ? portalText("core", "auth.6_digit_code_0c52ea") : portalText("core", "auth.recovery_code_3cb5fd")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="secondStepCode"
                  inputMode={secondStepFactor === "totp" ? "numeric" : undefined}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <ProblemReference reference={problemReference} />
            <Button variant="primary" type="submit" block disabled={answerSecondStep.isPending}>
              {answerSecondStep.isPending ? portalText("core", "auth.checking_820d60") : portalText("core", "auth.continue_2e0262")}
            </Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setSecondStepFactor(secondStepFactor === "totp" ? "recovery_code" : "totp");
                setCode("");
                setProblem(null);
              }}
            >
              {secondStepFactor === "totp" ? portalText("core", "auth.use_a_recovery_code_instead_104df4") : portalText("core", "auth.use_my_authenticator_app_instead_e5d89a")}
            </Button>
          </form>
        ) : providerConfirming ? (
          <form
            aria-labelledby="pd-provider-confirm-title"
            onSubmit={(event) => {
              event.preventDefault();
              setProblem(null);
              if (code.replace(/\s/g, "").length !== 6) {
                setProblem(() => (portalText("core", "auth.provider.code_wrong")));
                return;
              }
              confirmProvider.mutate();
            }}
          >
            <h1 id="pd-provider-confirm-title" tabIndex={-1}>{portalText("core", "auth.provider.confirm_title")}</h1>
            <p className="c-entry-code-note">{portalText("core", "auth.provider.confirm_lede")}</p>
            <Field label={portalText("core", "auth.6_digit_code_0c52ea")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="providerCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <ProblemReference reference={problemReference} />
            <Button variant="primary" type="submit" block disabled={confirmProvider.isPending || continueAfterProvider.isPending}>
              {confirmProvider.isPending ? portalText("core", "auth.checking_820d60") : portalText("core", "auth.continue_2e0262")}
            </Button>
            <Button variant="secondary" block disabled={resendProviderCode.isPending} onClick={() => resendProviderCode.mutate()}>
              {portalText("core", "auth.provider.resend")}
            </Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setProviderConfirming(false);
                setCode("");
                setProblem(null);
              }}
            >{portalText("core", "auth.provider.another_way")}</Button>
          </form>
        ) : verificationSentTo !== null ? (
          <div aria-labelledby="pd-verification-sent-title">
            <h1 id="pd-verification-sent-title" tabIndex={-1}>{portalText("core", "auth.entry.check_your_email")}</h1>
            <p className="c-entry-code-note">{portalText("core", "auth.verification_sent.lede", { value0: verificationSentTo })}</p>
            <p className="c-entry-code-note">{portalText("core", "auth.verification_sent.next")}</p>
            <Button
              variant="primary"
              block
              onClick={() => {
                setVerificationSentTo(null);
                switchMode("log-in");
              }}
            >
              {portalText("core", "auth.log_in_f7c400")}
            </Button>
            <p className="pd-fine">{portalText("core", "auth.verification_sent.spam")}</p>
          </div>
        ) : unscoped !== null && unscoped.length === 0 ? (
          <FinishSetup
            initial={signingUp ? accountDraft : emptyBusinessDraft()}
            emailAddress={emailAddress === "" ? null : emailAddress}
          />
        ) : unscoped !== null ? (
          <>
            <h1 tabIndex={-1}>{portalText("core", "auth.opening_your_business_7789f0")}</h1>
            {problem === null ? null : (
              <p className="wt-field-error" role="alert">
                {problem}
              </p>
            )}
            <ProblemReference reference={problemReference} />
            {unscoped.map((one) => (
              <Button
                key={one.accountId}
                variant="secondary"
                block
                disabled={choose.isPending}
                onClick={() => {
                  setProblem(null);
                  choose.mutate(one.accountId);
                }}
              >
                {one.accountName}
              </Button>
            ))}
          </>
        ) : signInCode !== null ? (
          <form
            aria-labelledby="pd-code-title"
            onSubmit={(event) => {
              event.preventDefault();
              setProblem(null);
              if (code.trim() === "") {
                setProblem(() => (portalText("core", "auth.enter_the_6_digit_code_from_the_email_029180")));
                return;
              }
              signInWithCode.mutate();
            }}
          >
            <h1 id="pd-code-title" tabIndex={-1}>{portalText("core", "auth.entry.check_your_email")}</h1>
            {identifier}
            <p className="c-entry-code-note">{portalText("core", "auth.if_this_address_signs_in_to_wetalk_a_6_digit_code_is_on_its_way_i_8fa474")}{" "}
              {new Date(signInCode.expiresAt).toLocaleTimeString(portalLocale() === "en" ? "en-GB" : "el-GR", { hour: "2-digit", minute: "2-digit" })}.
            </p>
            <Field label={portalText("core", "auth.sign_in_code_6af151")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="signInCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <ProblemReference reference={problemReference} />
            <Button variant="primary" type="submit" block disabled={signInWithCode.isPending}>
              {signInWithCode.isPending ? portalText("core", "auth.signing_in_c66b2a") : portalText("core", "auth.log_in_f7c400")}
            </Button>
            <Button variant="secondary" block disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>{portalText("core", "auth.send_a_new_code_91904a")}</Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setSignInCode(null);
                setCode("");
                setProblem(null);
              }}
            >{portalText("core", "auth.use_my_password_instead_b5501c")}</Button>
          </form>
        ) : (
          <>
            <PublicKicker>{portalText("core", "auth.entry.welcome")}</PublicKicker>
            <h1 tabIndex={-1}>{portalText("core", "auth.entry.title")}</h1>
            <p className="pd-lede">{portalText("core", "auth.entry.lede")}</p>

            <div className="pd-providers">
              <ProviderButton
                provider="google"
                available={providers.data?.google === true}
                returnPath={providerReturnPathOf(mode, offerCode)}
                noteId={providerNoteId}
              />
              <ProviderButton
                provider="microsoft"
                available={providers.data?.microsoft === true}
                returnPath={providerReturnPathOf(mode, offerCode)}
                noteId={providerNoteId}
              />
              {providers.data?.google === true && providers.data.microsoft === true ? null : (
                <p className="pd-provider-note" id={providerNoteId}>{portalText("core", "auth.entry.providers_soon")}</p>
              )}
            </div>
            {providerProblem === null || problem !== null ? null : (
              <p className="wt-field-error" role="alert">{providerProblemText(providerProblem)}</p>
            )}
            {providerReturn.ssoStart === null ? null : (
              <a className="btn btn-primary btn-block" href={providerReturn.ssoStart}>{portalText("core", "auth.provider.sso_continue")}</a>
            )}
            {continueAfterProvider.isPending ? <p className="c-entry-code-note" role="status">{portalText("core", "auth.provider.finishing")}</p> : null}

            <div className="pd-or" aria-hidden="true">{portalText("core", "auth.entry.or_with_email")}</div>

            <form
              onSubmit={discovery === null ? onIdentifier : onPassword}
              noValidate
              aria-label={signingUp && discovery !== null ? portalText("core", "auth.entry.create_account") : portalText("core", "auth.entry.or_with_email")}
            >
              {/*
                The sign-up trap (ADR 0071): out of sight, out of the tab order and of the accessibility tree. Its name
                matches no autofill heuristic (no company, website, url, name, email, phone or address), and the
                password managers' own opt-outs are set, so a browser or a manager never fills it for a person — a
                filled trap silently swallows the request (review item 5).
              */}
              <div className="wt-visually-hidden" aria-hidden="true">
                <input
                  type="text"
                  name="entry_marker"
                  id="pd-entry-marker"
                  tabIndex={-1}
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  value={entryMarker}
                  onChange={(event) => {
                    setEntryMarker(event.currentTarget.value);
                  }}
                />
              </div>
              <Field label={portalText("core", "auth.entry.work_email")} {...(problem === null || discovery !== null ? {} : { error: problem })}>
                {(control) => (
                  <div className="pd-input-icon">
                    <EnvelopeMark />
                    <Input
                      {...control}
                      name="emailAddress"
                      type="email"
                      value={emailAddress}
                      placeholder={portalText("core", "auth.you_business_com_48ac25")}
                      autoComplete="username"
                      onChange={(event) => {
                        setEmailAddress(event.currentTarget.value);
                        // A changed address is a new question: discovery answered for the old one.
                        if (discovery !== null) changeAddress();
                      }}
                    />
                  </div>
                )}
              </Field>

              {discovery === null ? null : !discovery.showPassword ? (
                <div className="pd-more" ref={more}>
                  <div className="c-entry-governed" tabIndex={-1}>
                    <Icon glyph={GLYPH.lockSimple} size="lg" tone="accent" />
                    <span>{portalText("core", "auth.your_organisation_signs_in_with_microsoft_so_there_is_no_password_10f4e8")}</span>
                  </div>
                  {discovery.startUrl === null ? null : (
                    <a className="btn btn-primary btn-block" href={discovery.startUrl}>{portalText("core", "auth.continue_with_6433fa")}{" "}{discovery.method === "microsoft" ? portalText("core", "auth.microsoft_11f324") : portalText("core", "auth.your_provider_adea16")}
                    </a>
                  )}
                </div>
              ) : (
                <div className="pd-more" ref={more}>
                  {signingUp ? <BusinessFields draft={accountDraft} onChange={setAccountDraft} /> : null}
                  <div className="pd-password">
                    <Field
                      label={portalText("core", "auth.password_8be3c9")}
                      {...(problem === null ? {} : { error: problem })}
                    >
                      {(control) => (
                        <Input
                          {...control}
                          name="password"
                          type="password"
                          // The design's placeholder, in both modes (design audit 2026-10-15), in place of a hint line.
                          placeholder={portalText("core", "auth.entry.password_placeholder")}
                          value={password}
                          autoComplete={signingUp ? "new-password" : "current-password"}
                          onChange={(event) => {
                            setPassword(event.currentTarget.value);
                          }}
                        />
                      )}
                    </Field>
                    {signingUp ? null : <Link className="pd-forgot" to="/forgot-password">{portalText("core", "auth.forgot_your_password_a2f060")}</Link>}
                  </div>
                </div>
              )}

              <ProblemReference reference={problemReference} />

              {discovery !== null && !discovery.showPassword ? null : (
                <button type="submit" className="pd-cta" disabled={discover.isPending || authenticate.isPending}>
                  {discovery === null
                    ? (discover.isPending ? portalText("core", "auth.checking_820d60") : portalText("core", "auth.entry.continue_with_email"))
                    : signingUp ? portalText("core", "auth.entry.create_account") : portalText("core", "auth.log_in_f7c400")}
                  <ArrowMark />
                </button>
              )}

              {codeAvailable && discovery !== null && discovery.showPassword ? (
                <Button
                  variant="secondary"
                  block
                  disabled={askForCode.isPending}
                  onClick={() => {
                    setProblem(null);
                    askForCode.mutate();
                  }}
                >
                  {askForCode.isPending ? portalText("core", "auth.sending_cf7655") : portalText("core", "auth.email_me_a_sign_in_code_instead_d078c2")}
                </Button>
              ) : null}
            </form>

            {discovery === null || !discovery.showPassword ? null : (
              <p className="pd-switch">
                {signingUp ? (
                  <>
                    {portalText("core", "auth.entry.have_an_account")}{" "}
                    <button type="button" className="pd-link-button" onClick={() => { switchMode("log-in"); }}>{portalText("core", "auth.log_in_f7c400")}</button>
                    {portalText("core", "auth._invited_by_a_colleague_731f80")}{" "}
                    <Link to="/join">{portalText("core", "auth.open_your_invitation_26dba3")}</Link>{" "}
                    {portalText("core", "auth._you_will_not_need_a_new_organization_7f9687")}
                  </>
                ) : (
                  <>
                    {portalText("core", "auth.new_here_b81f2e")}{" "}
                    <button type="button" className="pd-link-button" onClick={() => { switchMode("sign-up"); }}>{portalText("core", "auth.create_an_account_3f4f54")}</button>{" "}
                    {portalText("core", "auth.instead_656896")}
                  </>
                )}
              </p>
            )}

            <p className="pd-fine">
              {portalText("core", "auth.entry.agree_lead")}{" "}
              <a href={site("/terms/")}>{portalText("core", "auth.entry.terms")}</a>{" "}
              {portalText("core", "auth.entry.agree_join")}{" "}
              <a href={site("/privacy/")}>{portalText("core", "auth.entry.privacy")}</a>
              {portalText("core", "auth.entry.agree_end")}
            </p>
          </>
        )}
      </div>
    </PublicFrame>
  );
}

/** The request id of a failure on WeTalk's side, in small print, for a person who contacts support. */
function ProblemReference({ reference }: { readonly reference: string | null }): ReactNode {
  usePortalTranslation("core");
  if (reference === null) return null;
  return (
    <p className="c-entry-reference">{portalText("core", "auth.reference_45c3dc")}{" "}<Mono>{reference}</Mono>
    </p>
  );
}

// ── plan-028: Google and Microsoft sign-in (design §39.11) ───────────────────────────────────────────────────────

/** Every refusal a provider's return may name (the API's `ProviderProblem`), each with its own message. */
const PROVIDER_PROBLEM_TEXT = {
  not_available: "auth.provider.problem.not_available",
  cancelled: "auth.provider.problem.cancelled",
  expired: "auth.provider.problem.expired",
  browser: "auth.provider.problem.browser",
  admin_consent: "auth.provider.problem.admin_consent",
  provider_unavailable: "auth.provider.problem.provider_unavailable",
  token_refused: "auth.provider.problem.token_refused",
  organisation_signs_in_with_sso: "auth.provider.problem.organisation_signs_in_with_sso",
  organisation_not_configured: "auth.provider.problem.organisation_not_configured",
  identity_in_use: "auth.provider.problem.identity_in_use",
  signup_limit_reached: "auth.provider.problem.signup_limit_reached",
  address_confirmation_unavailable: "auth.provider.problem.address_confirmation_unavailable",
  person_not_active: "auth.provider.problem.person_not_active",
  no_access: "auth.provider.problem.no_access",
  code_invalid: "auth.provider.problem.code_invalid",
  too_many_codes: "auth.provider.problem.too_many_codes",
} as const;
export type ProviderProblemCode = keyof typeof PROVIDER_PROBLEM_TEXT;

function isProviderProblem(value: string): value is ProviderProblemCode {
  return Object.hasOwn(PROVIDER_PROBLEM_TEXT, value);
}

/** The message for a provider's refusal. */
export function providerProblemText(problem: ProviderProblemCode): string {
  return portalText("core", PROVIDER_PROBLEM_TEXT[problem]);
}

/** The organization start discovery builds; anything else in `sso_start` is ignored, never followed. */
const SSO_START_SHAPE = /^\/v1\/auth\/microsoft\/start\?d=[a-z0-9.-]{4,253}$/;

export interface ProviderReturn {
  readonly signedIn: boolean;
  readonly confirm: boolean;
  readonly problem: ProviderProblemCode | null;
  readonly ssoStart: string | null;
}

/** plan-034 WAPP-APPSIGNIN: whether this sign-in is being made for the wetalk app's consent page (a fresh hold). */
function signingInForApp(): boolean {
  return appSignInReturnOf(tabStorage(), Date.now()) !== null;
}

/** What a provider's return asked for (`?provider=signed_in`, `?confirm=provider`, `?problem=…&sso_start=…`). */
export function providerReturnOf(search: string): ProviderReturn {
  const query = new URLSearchParams(search);
  const problem = query.get("problem");
  const ssoStart = query.get("sso_start");
  return {
    signedIn: query.get("provider") === "signed_in",
    confirm: query.get("confirm") === "provider",
    problem: problem !== null && isProviderProblem(problem) ? problem : null,
    ssoStart: ssoStart !== null && SSO_START_SHAPE.test(ssoStart) ? ssoStart : null,
  };
}

/** Where the provider brings the browser back: this same card, told it is signed in (the chosen plan kept). */
export function providerReturnPathOf(mode: EntryIntent, offerCode: string | undefined): string {
  const query = new URLSearchParams({ provider: "signed_in" });
  if (offerCode !== undefined) query.set("plan", offerCode);
  return `${mode === "sign-up" ? "/sign-up" : "/log-in"}?${query.toString()}`;
}

function ProviderButton(props: {
  readonly provider: "google" | "microsoft";
  readonly available: boolean;
  readonly returnPath: string;
  readonly noteId: string;
}): ReactNode {
  const mark = props.provider === "google" ? <GoogleMark /> : <MicrosoftMark />;
  const label = portalText("core", props.provider === "google" ? "auth.entry.google" : "auth.entry.microsoft");
  if (!props.available) {
    return (
      <button type="button" className="pd-provider" disabled aria-describedby={props.noteId}>
        {mark}
        {label}
      </button>
    );
  }
  // A top-level navigation: the start route answers a redirect to the provider and sets the browser-binding cookie.
  return (
    <a className="pd-provider" href={`/v1/auth/${props.provider}/start?return=${encodeURIComponent(props.returnPath)}`}>
      {mark}
      {label}
    </a>
  );
}
